import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useOrganisations } from "../../Auth/useOrganisations";
import { getTodayTableSessions, cancelOrder } from "../../Services/CartApi";
import { useUser } from "../../Auth/useUser";
import OrderDetailsModal from "../OrderDetails";

const CaisseOrders = ({ selectedTable }) => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCart, setSelectedCart] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [cancelingId, setCancelingId] = useState(null);
  const activeJournal = JSON.parse(localStorage.getItem("activeJournal"));
  const infoUser = useUser();
  const user = infoUser.result.id;
  const [
    organisations,
    setOrganisations,
    usedOrganisations,
    setUsedOrganisations,
  ] = useOrganisations();

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await getTodayTableSessions(
        user,
        activeJournal._id || activeJournal.id,
      );
      if (response.data.success !== "0") {
        setSessions(response.data.data.allSessions || []);
      }
    } catch (error) {
      console.error("Error loading sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, [usedOrganisations]);

  const calculateSessionTotal = (carts) => {
    if (!carts || !Array.isArray(carts)) return 0;
    return carts.reduce((total, cart) => {
      if (cart.produits && Array.isArray(cart.produits)) {
        return (
          total +
          cart.produits.reduce((cartTotal, produit) => {
            return (
              cartTotal + (produit.netPrice || produit.price * produit.quantity)
            );
          }, 0)
        );
      }
      return total;
    }, 0);
  };

  const isSessionPaid = (session) => {
    return session.status === "inactive" || session.paid === true;
  };

  const isSessionCanceled = (session) => {
    return session.status === "canceled";
  };

  // Same shape/pattern as ClientDetailModal's OrdersTab, so this renders the
  // exact same order-details modal as the admin Orders page.
  const handleViewDetails = (session) => {
    const firstCart = session.carts?.[0] || {};
    const cartForModal = {
      ...firstCart,
      session: session.session,
      payments: session.payments || [],
      tableNumber:
        session.session?.table_id?.table_number ||
        session.session?.table_id?.numero ||
        firstCart.table_id?.numero,
    };
    setSelectedCart(cartForModal);
    setDetailOpen(true);
  };

  const handleCancelOrder = async (session) => {
    const sessionId = session?.session?._id || session?.session?.id;
    if (!sessionId || cancelingId) return;

    const result = await Swal.fire({
      title: "Annuler la commande ?",
      text: "Les produits repartent en stock et le montant en espèces sera retiré du fond de caisse.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Oui, annuler",
      cancelButtonText: "Retour",
    });
    if (!result.isConfirmed) return;

    setCancelingId(sessionId);
    try {
      const response = await cancelOrder({
        table_session_id: sessionId,
        caissier_id: user,
      });

      if (response?.data?.success) {
        const updatedJournal = response.data.data?.journal;
        if (updatedJournal) {
          localStorage.setItem("activeJournal", JSON.stringify(updatedJournal));
        }
        toast.success("Commande annulée : stock restitué et fond mis à jour");
        loadSessions();
      } else {
        toast.error(
          response?.data?.errorMessage ||
            "Erreur lors de l'annulation de la commande",
        );
      }
    } catch (error) {
      console.error("Error canceling order:", error);
      toast.error(
        error.response?.data?.errorMessage ||
          "Erreur lors de l'annulation de la commande",
      );
    } finally {
      setCancelingId(null);
    }
  };

  return (
    <div className="orders-wrapper">
      {loading ? (
        <div className="loading-screen">
          <p>Chargement des commandes...</p>
        </div>
      ) : (
        <div className="orders-container">
          {sessions.length === 0 ? (
            <div className="no-orders">
              <h4>Aucune commande aujourd'hui</h4>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Heure</th>
                    {organisations[0].type === "Restaurant" && (
                      <>
                        <th>Ticket</th>
                        <th>Table</th>
                        <th>Serveur</th>
                      </>
                    )}
                    <th>Caissier</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {sessions.map((session) => {
                    const total = calculateSessionTotal(session.carts);
                    const isPaid = isSessionPaid(session.session);
                    const isCanceled = isSessionCanceled(session.session);

                    return (
                      <tr
                        key={session.session._id}
                        className={
                          selectedTable &&
                          session.table_id &&
                          selectedTable._id === session.table_id._id
                            ? "row-selected"
                            : ""
                        }
                      >
                        <td
                          onClick={() => handleViewDetails(session)}
                          className="clickable"
                        >
                          {session.session.code}
                        </td>
                        <td>
                          {new Date(
                            session.session.start_time,
                          ).toLocaleTimeString("fr-FR", {
                            hour: "2-digit",
                            minute: "2-digit",
                            timeZone:
                              Intl.DateTimeFormat().resolvedOptions().timeZone,
                          })}
                        </td>
                        {organisations[0].type === "Restaurant" && (
                          <>
                            <td onClick={() => console.log(session)}>
                              {session.carts.map((cart) => (
                                <span key={cart.id} className="ticket-badge">
                                  {cart.numero}
                                </span>
                              ))}
                            </td>
                            <td>
                              {session.session.table_id?.default
                                ? "Comptoir"
                                : ` ${session.session.table_id?.table_number}`}
                            </td>

                            <td>
                              <b>{session.session.serveur_id?.nom || "-"} </b>
                            </td>
                          </>
                        )}
                        <td>
                          <b>
                            {session.session.caissier_id?.nom ||
                              session.paymentCashier?.nom ||
                              "-"}{" "}
                          </b>
                        </td>
                        <td>
                          <b>{total.toFixed(3)} TND</b>
                        </td>
                        <td>
                          <span
                            className={`chip ${
                              isCanceled
                                ? "chip-canceled"
                                : isPaid
                                ? "chip-paid"
                                : "chip-unpaid"
                            }`}
                          >
                            {isCanceled
                              ? "Annulé"
                              : isPaid
                              ? "Payé"
                              : "Non Payé"}
                          </span>
                        </td>
                        <td>
                          {isPaid && !isCanceled && (
                            <button
                              className="btn-cancel-order"
                              onClick={() => handleCancelOrder(session)}
                              disabled={cancelingId === session.session._id}
                              title="Annuler la commande"
                            >
                              <i className="fa fa-ban" />{" "}
                              {cancelingId === session.session._id
                                ? "..."
                                : "Annuler"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="summary-footer">
            <span>Total: {sessions.length} commandes(s)</span>

            {organisations[0].type === "Restaurant" && (
              <div className="summary-footer">
                <span>
                  Actives:{" "}
                  {sessions.filter((s) => s.session.status === "active").length}{" "}
                  | Cloturés:{" "}
                  {
                    sessions.filter((s) => s.session.status === "inactive")
                      .length
                  }
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        .orders-wrapper {
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .orders-container {
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .table-wrapper {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .orders-table {
          width: 100%;
          border-collapse: collapse;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .orders-table thead {
          flex-shrink: 0;
        }

        .orders-table tbody {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
        }

        .orders-table thead,
        .orders-table tbody tr {
          display: table;
          table-layout: fixed;
          width: 100%;
        }

        .summary-footer {
          flex-shrink: 0;
          padding: 10px;
          background-color: #f5f5f5;
          border-top: 1px solid #ddd;
          display: flex;
          justify-content: space-between;
        }

        .no-orders {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-cancel-order {
          border: none;
          border-radius: 6px;
          padding: 5px 10px;
          font-size: 12px;
          font-weight: 600;
          background: #fdecea;
          color: #b42318;
          cursor: pointer;
        }

        .btn-cancel-order:hover {
          background: #fbdad6;
        }

        .btn-cancel-order:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>

      <OrderDetailsModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        cart={selectedCart}
        currency="TND"
      />
    </div>
  );
};

export default CaisseOrders;
